import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { Bars3Icon } from '@heroicons/react/24/outline';
import { useState } from 'react';

interface OrderGroup {
  id: string;
  name: string;
  items: { id: string; name: string }[];
}

export default function NavigationOrderEditor({ groups, disabled, onReorder }: {
  groups: OrderGroup[];
  disabled: boolean;
  onReorder: (group: string, ids: string[]) => Promise<void>;
}) {
  const [error, setError] = useState('');
  return (
    <div className="px-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
      <p className="py-2">Drag items within each group to reorder. Changes save automatically.</p>
      {error && <p role="alert" className="py-2 text-red-300">{error}</p>}
      <DragDropContext onDragEnd={async ({ source, destination }) => {
        if (!destination || disabled || source.droppableId !== destination.droppableId || source.index === destination.index) return;
        const group = groups.find(group => group.id === source.droppableId);
        if (!group) return;
        const ids = group.items.map(item => item.id);
        const [moved] = ids.splice(source.index, 1);
        ids.splice(destination.index, 0, moved);
        setError('');
        try { await onReorder(group.id, ids); }
        catch { setError('Could not save navigation order. Please try again.'); }
      }}>
        {groups.map(group => (
          <details key={group.id} open={group.id === 'main-nav' || group.id === 'favorites'} className="mb-3">
            <summary className="cursor-pointer py-2 font-semibold">{group.name}</summary>
            <Droppable droppableId={group.id} type={group.id}>
              {provided => (
                <ul ref={provided.innerRef} {...provided.droppableProps} aria-label={group.name}>
                  {group.items.map((item, index) => (
                    <Draggable key={item.id} draggableId={JSON.stringify([group.id, item.id])} index={index} isDragDisabled={disabled}>
                      {(drag, snapshot) => (
                        <li ref={drag.innerRef} {...drag.draggableProps}
                          className={`flex items-center gap-2 px-2 py-2 mb-1 rounded border border-white/10 ${snapshot.isDragging ? 'bg-[var(--bg-secondary)] shadow-lg' : 'bg-white/5'}`}>
                          <span {...drag.dragHandleProps} aria-label={`Drag ${item.name}`} className="cursor-grab shrink-0">
                            <Bars3Icon className="w-5 h-5" />
                          </span>
                          <span>{item.name}</span>
                        </li>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </ul>
              )}
            </Droppable>
          </details>
        ))}
      </DragDropContext>
    </div>
  );
}
